import { Users, User, Baby, GraduationCap, BookOpen, School, GripVertical, Plus, Trash2, Check } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useChurch } from "@/hooks/useChurch";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useSubscription } from "@/hooks/useSubscription";
import { useIsMobile } from "@/hooks/use-mobile";

type Stats = {
  total: number;
  women: number;
  men: number;
  children: number;
  youth: number;
  adult: number;
  senior: number;
  preschool: number;
  elementary: number;
  middle: number;
  highSchool: number;
  college: number;
  workingAdults: number;
  unemployedAdults: number;
};

type InsightCardDef = {
  id: string;
  label: string;
  icon: any;
  rgb: string;
  sub?: string;
  getValue: (stats: Stats) => number;
};

const CARD_DEFS: InsightCardDef[] = [
  { id: "total", label: "Total Members", icon: Users, rgb: "9,102,241", getValue: (s) => s.total },
  { id: "workingAdults", label: "Total Working Adults", icon: Users, rgb: "9,102,241", sub: "Age 18+", getValue: (s) => s.workingAdults },
  { id: "unemployedAdults", label: "Total Unemployed", icon: Users, rgb: "9,102,241", sub: "Age 18+", getValue: (s) => s.unemployedAdults },
  { id: "women", label: "Total Female", icon: User, rgb: "9,102,241", getValue: (s) => s.women },
  { id: "men", label: "Total Male", icon: User, rgb: "9,102,241", getValue: (s) => s.men },
  { id: "children", label: "Children", icon: Baby, rgb: "9,102,241", sub: "Age 0-12", getValue: (s) => s.children },
  { id: "youth", label: "Youth", icon: Users, rgb: "9,102,241", sub: "Age 13-25", getValue: (s) => s.youth },
  { id: "adult", label: "Adult", icon: Users, rgb: "9,102,241", sub: "Age 26-55", getValue: (s) => s.adult },
  { id: "senior", label: "Senior / Elder", icon: Users, rgb: "9,102,241", sub: "Age 56+", getValue: (s) => s.senior },
  { id: "preschool", label: "Preschool Students", icon: Baby, rgb: "9,102,241", getValue: (s) => s.preschool },
  { id: "elementary", label: "Elementary Students", icon: BookOpen, rgb: "9,102,241", getValue: (s) => s.elementary },
  { id: "middle", label: "Middle School Students", icon: School, rgb: "9,102,241", getValue: (s) => s.middle },
  { id: "highSchool", label: "High School Students", icon: GraduationCap, rgb: "9,102,241", getValue: (s) => s.highSchool },
  { id: "college", label: "College Students", icon: GraduationCap, rgb: "9,102,241", getValue: (s) => s.college },
];

function getAge(dob: string | null): number | null {
  if (!dob) return null;
  const birth = new Date(dob + "T00:00:00");
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

const DEFAULT_VISIBLE_DESKTOP = [
  "total",
  "men",
  "women",
  "college",
  "highSchool",
  "middle",
  "elementary",
];

const DEFAULT_VISIBLE_MOBILE = [
  "total",
  "men",
  "women",
  "college",
  "highSchool",
  "middle",
  "elementary",
];

function arraysMatchInOrder(left: string[], right: string[]) {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

export default function CommunityInsights() {
  const { profile } = useChurch();
  const { memberCount, memberLimit } = useSubscription();
  const isMobile = useIsMobile();
  const churchId = profile?.id;
  const storageBaseKey = `community-insights-layout:${churchId || "default"}`;
  const storageKey = `${storageBaseKey}:${isMobile ? "mobile" : "desktop"}`;
  const allCardIds = useMemo(() => CARD_DEFS.map((card) => card.id), []);
  const defaultVisibleCardIds = useMemo(
    () => (isMobile ? DEFAULT_VISIBLE_MOBILE : DEFAULT_VISIBLE_DESKTOP),
    [isMobile]
  );
  const [visibleCardIds, setVisibleCardIds] = useState<string[]>(defaultVisibleCardIds);
  const [hiddenCardIds, setHiddenCardIds] = useState<string[]>(allCardIds.filter((id) => !defaultVisibleCardIds.includes(id)));
  const [editMode, setEditMode] = useState(false);
  const [draggedCardId, setDraggedCardId] = useState<string | null>(null);
  const [dragHiddenId, setDragHiddenId] = useState<string | null>(null);
  const [lastDragOverId, setLastDragOverId] = useState<string | null>(null);
  const [layoutLoaded, setLayoutLoaded] = useState(false);
  const [lastTapAt, setLastTapAt] = useState(0);
  const draggedCardIdRef = useRef<string | null>(null);
  const lastDragOverIdRef = useRef<string | null>(null);

  const { data: members = [] } = useQuery({
    queryKey: ["dashboard_all_members", churchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("members")
        .select("id, gender, date_of_birth, education_status, employment_status")
        .eq("church_id", churchId!);
      if (error) throw error;
      return data || [];
    },
    enabled: !!churchId,
  });

  const stats = useMemo(() => {
    const ages = members.map((m) => ({ ...m, age: getAge(m.date_of_birth) }));
    return {
      total: members.length,
      women: members.filter((m) => m.gender === "Female").length,
      men: members.filter((m) => m.gender === "Male").length,
      children: ages.filter((m) => m.age !== null && m.age >= 0 && m.age <= 12).length,
      youth: ages.filter((m) => m.age !== null && m.age >= 13 && m.age <= 25).length,
      adult: ages.filter((m) => m.age !== null && m.age >= 26 && m.age <= 55).length,
      senior: ages.filter((m) => m.age !== null && m.age >= 56).length,
      preschool: members.filter((m) => m.education_status === "Preschool").length,
      elementary: members.filter((m) => m.education_status === "Elementary School").length,
      middle: members.filter((m) => m.education_status === "Middle School").length,
      highSchool: members.filter((m) => m.education_status === "High School").length,
      college: members.filter((m) => m.education_status === "College / University" || m.education_status === "Graduate School").length,
      workingAdults: ages.filter((m) => m.age !== null && m.age > 18 && ["Employed Full-Time", "Employed Part-Time", "Self-Employed"].includes((m as any).employment_status)).length,
      unemployedAdults: ages.filter((m) => m.age !== null && m.age > 18 && (m as any).employment_status === "Unemployed").length,
    };
  }, [members]);

  const cardsById = useMemo(() => {
    const entries = CARD_DEFS.map((card) => [
      card.id,
      {
        ...card,
        value: card.getValue(stats),
      },
    ] as const);
    return new Map(entries);
  }, [stats]);

  useEffect(() => {
    const defaultHiddenCardIds = allCardIds.filter((id) => !defaultVisibleCardIds.includes(id));

    const applyDefaultLayout = () => {
      setVisibleCardIds(defaultVisibleCardIds);
      setHiddenCardIds(defaultHiddenCardIds);
      setLayoutLoaded(true);
    };

    const parseLayout = (raw: string | null) => {
      if (!raw) return null;

      try {
        const parsed = JSON.parse(raw) as { visibleCardIds?: string[]; hiddenCardIds?: string[] };
        const validVisible = (parsed.visibleCardIds || []).filter((id) => allCardIds.includes(id));
        if (validVisible.length === 0) return null;

        const validHidden = (parsed.hiddenCardIds || []).filter((id) => allCardIds.includes(id) && !validVisible.includes(id));
        const missing = allCardIds.filter((id) => !validVisible.includes(id) && !validHidden.includes(id));

        return {
          visibleCardIds: validVisible,
          hiddenCardIds: [...validHidden, ...missing],
        };
      } catch {
        return null;
      }
    };

    const savedLayout = parseLayout(localStorage.getItem(storageKey));
    if (savedLayout) {
      setVisibleCardIds(savedLayout.visibleCardIds);
      setHiddenCardIds(savedLayout.hiddenCardIds);
      setLayoutLoaded(true);
      return;
    }

    const legacyLayout = parseLayout(localStorage.getItem(storageBaseKey));
    if (legacyLayout) {
      const shouldUseMobileDefault = isMobile && arraysMatchInOrder(legacyLayout.visibleCardIds, DEFAULT_VISIBLE_DESKTOP);

      if (!shouldUseMobileDefault) {
        setVisibleCardIds(legacyLayout.visibleCardIds);
        setHiddenCardIds(legacyLayout.hiddenCardIds);
        setLayoutLoaded(true);
        return;
      }
    }

    applyDefaultLayout();
  }, [allCardIds, defaultVisibleCardIds, isMobile, storageBaseKey, storageKey]);

  useEffect(() => {
    if (!layoutLoaded) return;
    localStorage.setItem(storageKey, JSON.stringify({ visibleCardIds, hiddenCardIds }));
  }, [hiddenCardIds, layoutLoaded, storageKey, visibleCardIds]);

  const visibleCards = useMemo(
    () => visibleCardIds.map((id) => cardsById.get(id)).filter(Boolean),
    [visibleCardIds, cardsById]
  );

  const hiddenCards = useMemo(
    () => hiddenCardIds.map((id) => cardsById.get(id)).filter(Boolean),
    [hiddenCardIds, cardsById]
  );

  const moveCard = (draggedId: string, targetId: string) => {
    if (draggedId === targetId) return;
    setVisibleCardIds((prev) => {
      const from = prev.indexOf(draggedId);
      const to = prev.indexOf(targetId);
      if (from === -1 || to === -1) return prev;
      const next = [...prev];
      next.splice(from, 1);
      next.splice(to, 0, draggedId);
      return next;
    });
  };

  const removeCard = (cardId: string) => {
    setVisibleCardIds((prev) => prev.filter((id) => id !== cardId));
    setHiddenCardIds((prev) => (prev.includes(cardId) ? prev : [...prev, cardId]));
  };

  const addCard = (cardId: string) => {
    setHiddenCardIds((prev) => prev.filter((id) => id !== cardId));
    setVisibleCardIds((prev) => (prev.includes(cardId) ? prev : [...prev, cardId]));
  };

  const handleMobileDoubleTap = (event: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile || !editMode) return;

    const target = event.target as HTMLElement;
    if (target.closest('[data-insight-card-zone="true"]')) return;
    if (target.closest('[data-edit-control="true"]')) return;

    const now = Date.now();
    if (now - lastTapAt < 320) {
      setEditMode(false);
      setLastTapAt(0);
      return;
    }

    setLastTapAt(now);
  };

  const handleMobileTouchStart = (event: React.TouchEvent<HTMLDivElement>, cardId: string) => {
    if (!isMobile || !editMode) return;

    const target = event.target as HTMLElement;
    if (target.closest('[data-edit-control="true"]')) return;

    setDraggedCardId(cardId);
    draggedCardIdRef.current = cardId;
    setLastDragOverId(null);
    lastDragOverIdRef.current = null;
    requestAnimationFrame(() => setDragHiddenId(cardId));
  };

  const clearDragState = () => {
    setDraggedCardId(null);
    draggedCardIdRef.current = null;
    setDragHiddenId(null);
    setLastDragOverId(null);
    lastDragOverIdRef.current = null;
  };

  useEffect(() => {
    if (!isMobile || !editMode || !draggedCardId) return;

    const handleWindowTouchMove = (event: TouchEvent) => {
      const activeDraggedCardId = draggedCardIdRef.current;
      if (!activeDraggedCardId) return;

      const touch = event.touches[0];
      if (!touch) return;

      event.preventDefault();
      const elementAtPoint = document.elementFromPoint(touch.clientX, touch.clientY) as HTMLElement | null;
      const targetCard = elementAtPoint?.closest("[data-insight-card-id]") as HTMLElement | null;
      const targetId = targetCard?.dataset.insightCardId;

      if (!targetId || targetId === activeDraggedCardId || lastDragOverIdRef.current === targetId) return;

      moveCard(activeDraggedCardId, targetId);
      setLastDragOverId(targetId);
      lastDragOverIdRef.current = targetId;
    };

    const handleWindowTouchEnd = () => {
      clearDragState();
    };

    window.addEventListener("touchmove", handleWindowTouchMove, { passive: false });
    window.addEventListener("touchend", handleWindowTouchEnd);
    window.addEventListener("touchcancel", handleWindowTouchEnd);

    return () => {
      window.removeEventListener("touchmove", handleWindowTouchMove);
      window.removeEventListener("touchend", handleWindowTouchEnd);
      window.removeEventListener("touchcancel", handleWindowTouchEnd);
    };
  }, [draggedCardId, editMode, isMobile]);

  const usageLimit = memberLimit && memberLimit > 0 ? memberLimit : 5000;
  const usagePercent = usageLimit > 0 ? Math.min(100, Math.round((memberCount / usageLimit) * 100)) : 0;

  return (
    <div
      className="space-y-3"
      onDoubleClick={(event) => {
        if (!editMode) return;
        const target = event.target as HTMLElement;
        if (target.closest('[data-insight-card-zone="true"]')) return;
        setEditMode(false);
      }}
      onTouchEndCapture={handleMobileDoubleTap}
    >
      {isMobile && (
        <div className="flex items-center justify-end">
          {editMode ? (
            <Button size="sm" variant="outline" onClick={() => setEditMode(false)}>
              Done
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setEditMode(true)}>
              Edit Cards
            </Button>
          )}
        </div>
      )}

      <div className="grid gap-3 lg:grid-cols-[230px_minmax(0,1fr)]">
        <Card className="border-slate-100 min-h-[184px]" data-insight-card-zone="true">
          <CardContent className="p-3 flex items-center justify-center h-full">
            <div className="w-full flex items-center justify-center">
            <div className="relative w-36 h-36">
              <svg viewBox="0 0 160 160" className="w-full h-full">
                <circle cx="80" cy="80" r="66" fill="none" stroke="#dbeafe" strokeWidth="14" />
                <circle
                  cx="80"
                  cy="80"
                  r="66"
                  fill="none"
                  stroke="#1e3a8a"
                  strokeWidth="14"
                  strokeDasharray={`${(usagePercent / 100) * 2 * Math.PI * 66} ${2 * Math.PI * 66}`}
                  strokeLinecap="round"
                  transform="rotate(-90 80 80)"
                  className="transition-all duration-150"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <p className="text-xl font-bold text-slate-800 leading-none">
                  {memberCount.toLocaleString()} / {usageLimit.toLocaleString()}
                </p>
                <p className="text-[11px] font-semibold tracking-wide uppercase text-muted-foreground mt-1">Members</p>
              </div>
            </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-3 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {visibleCards.map((card) => {
            if (!card) return null;
            const Icon = card.icon;
            return (
              <Card
                key={card.id}
                data-insight-card-id={card.id}
                data-insight-card-zone="true"
                className={cn(
                  "group relative border-slate-100",
                  editMode && !isMobile && "cursor-grab active:cursor-grabbing",
                  editMode && isMobile && "touch-none",
                  editMode && isMobile && "ring-1 ring-primary/25",
                  dragHiddenId === card.id && "opacity-0"
                )}
                draggable={editMode && !isMobile}
                onDragStart={() => {
                  if (!editMode || isMobile) return;
                  setDraggedCardId(card.id);
                  setLastDragOverId(null);
                  requestAnimationFrame(() => setDragHiddenId(card.id));
                }}
                onDragOver={(e) => {
                  if (isMobile) return;
                  e.preventDefault();
                  if (!draggedCardId || draggedCardId === card.id) return;
                  if (lastDragOverId === card.id) return;
                  moveCard(draggedCardId, card.id);
                  setLastDragOverId(card.id);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setLastDragOverId(null);
                }}
                onDragLeave={() => {
                  if (lastDragOverId === card.id) setLastDragOverId(null);
                }}
                onDragEnd={() => {
                  clearDragState();
                }}
                onTouchStart={(event) => handleMobileTouchStart(event, card.id)}
              >
                {editMode && (
                  <button
                    type="button"
                    className="absolute right-7 top-1 z-10 p-1 rounded-md bg-background/90 border border-border transition-colors hover:bg-primary/10"
                    aria-label="Done editing cards"
                    onClick={() => setEditMode(false)}
                    data-edit-control="true"
                  >
                    <Check className="w-3.5 h-3.5 text-primary" />
                  </button>
                )}

                <button
                  type="button"
                  className={cn(
                    "absolute right-1 top-1 z-10 p-1 transition-opacity",
                    !isMobile && editMode && "cursor-grab active:cursor-grabbing",
                    isMobile
                      ? (editMode ? "opacity-100" : "opacity-0")
                      : (editMode ? "opacity-100" : "opacity-0 group-hover:opacity-100")
                  )}
                  aria-label="Drag to rearrange"
                  onClick={() => setEditMode(true)}
                  data-edit-control="true"
                >
                  <GripVertical className="w-3.5 h-3.5 text-muted-foreground" />
                </button>

                {editMode && visibleCardIds.length > 1 && (
                  <button
                    type="button"
                    className="absolute left-1 top-1 z-10 p-1 rounded-md bg-background/90 border border-border hover:bg-destructive/10"
                    aria-label={`Remove ${card.label}`}
                    onClick={() => removeCard(card.id)}
                    data-edit-control="true"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-destructive" />
                  </button>
                )}

                <CardContent className="pt-3 pb-3 px-3 flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: `rgba(${card.rgb}, 0.12)`,
                      }}
                    >
                      <Icon className="w-3.5 h-3.5" style={{ color: `rgb(${card.rgb})` }} />
                    </div>
                    <p className="font-bold text-foreground leading-tight text-base">
                      {card.value}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-muted-foreground leading-tight">{card.label}</p>
                    {card.sub && (
                      <p className="text-[10px] text-muted-foreground/70">{card.sub}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {editMode && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="rounded-lg bg-primary/5 hover:bg-primary/10 transition-colors min-h-[92px] flex flex-col items-center justify-center gap-1"
                  aria-label="Add another card"
                  data-edit-control="true"
                  data-insight-card-zone="true"
                >
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                    <Plus className="w-5 h-5 text-primary" />
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
                {hiddenCards.length === 0 ? (
                  <DropdownMenuItem disabled className="text-[11px]">All cards are already shown</DropdownMenuItem>
                ) : (
                  hiddenCards.map((card) => (
                    <DropdownMenuItem key={card!.id} onClick={() => addCard(card!.id)} className="text-[11px]">
                      {card!.label}
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {editMode && isMobile && (
        <p className="px-1 text-[11px] font-medium text-muted-foreground">
          Double-tap anywhere outside the controls to save this card layout.
        </p>
      )}
    </div>
  );
}
